import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, CheckCircle2, XCircle, Mail, Phone, MapPin, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format, humanizeStatus } from "@/lib/brand-tokens";
import { applications, getApplicationById, getAdminById, updateApplicationStatus } from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { toast } from "sonner";

const REJECT_TEMPLATES = [
  { id: "source", label: "Source-of-cards ineligible", text: "Source of cards does not meet eligibility (reseller resale chain, anonymous trade, or unverifiable origin)." },
  { id: "kyc",    label: "KYC could not verify",       text: "Identity verification was unable to confirm your information against authoritative sources." },
  { id: "geo",    label: "Geographic ineligibility",   text: "We currently accept members residing in the United States only." },
  { id: "other",  label: "Other (custom)",             text: "" },
];

// ADM-06 — Application detail
export default function ApplicationDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const baseApp = getApplicationById(appId) || applications[0];
  const app = baseApp; // always live from the store
  const reviewer = app.reviewerId ? getAdminById(app.reviewerId) : null;
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTpl, setRejectTpl] = useState("source");
  const [rejectText, setRejectText] = useState(REJECT_TEMPLATES[0].text);

  const onTpl = (id) => {
    setRejectTpl(id);
    const t = REJECT_TEMPLATES.find((x) => x.id === id);
    setRejectText(t?.text || "");
  };

  const approve = () => {
    setApproveOpen(false);
    updateApplicationStatus(app.id, "ApprovedAwaitingKYC");
    toast.success(`Approved ${app.firstName} ${app.lastName}`, { description: "KYC link sent. Member will appear in onboarding queue." });
    setTimeout(() => navigate("/admin/applications"), 800);
  };
  const reject = () => {
    setRejectOpen(false);
    updateApplicationStatus(app.id, "Rejected", { rejectReason: rejectText });
    toast.error(`Rejected ${app.firstName} ${app.lastName}`, { description: rejectText.slice(0, 80) });
    setTimeout(() => navigate("/admin/applications"), 800);
  };

  const isDecided = app.status === "Rejected" || app.status === "ActiveMember";

  return (
    <div data-testid="application-detail-page" className="space-y-5 pb-10">
      <Breadcrumbs items={[
        { label: "Admin", to: "/admin/dashboard" },
        { label: "Applications", to: "/admin/applications" },
        { label: `${app.firstName} ${app.lastName}` },
      ]} />

      {/* Header */}
      <div className="bg-surface border border-line rounded-xl p-6 flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-full bg-brand-50 text-brand-700 grid place-items-center text-h2 font-brand font-semibold">
            {app.firstName[0]}{app.lastName[0]}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-brand text-h1 text-ink font-semibold">{app.firstName} {app.lastName}</h1>
              <StatusBadge entity="application" status={app.status} dot />
            </div>
            <div className="mt-1 text-caption text-ink-subtle">
              Applied {format.dateTime(app.appliedAt)} · via {app.referralSource}
            </div>
          </div>
        </div>
        {!isDecided && (
          <div className="flex items-center gap-2">
            <Gated action="applications.reject">
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="reject-app-btn"
                      className="border-red-200 text-red-700 hover:bg-red-50">
                <XCircle className="size-4 mr-1.5" /> Reject
              </Button>
            </Gated>
            <Gated action="applications.approve">
              <Button onClick={() => setApproveOpen(true)} data-testid="approve-app-btn"
                      className="bg-brand-700 hover:bg-brand-800 text-white">
                <CheckCircle2 className="size-4 mr-1.5" /> Approve
              </Button>
            </Gated>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main */}
        <div className="lg:col-span-2 space-y-5">
          {/* Profile */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Applicant profile</h2>
            <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-body-sm">
              <Field icon={<Mail className="size-3.5" />} label="Email" value={app.email} />
              <Field icon={<Phone className="size-3.5" />} label="Phone" value={format.phone(app.phone)} />
              <Field icon={<MapPin className="size-3.5" />} label="Location" value={`${app.city}, ${app.state}`} />
              <Field icon={<FileText className="size-3.5" />} label="Volume estimate" value={format.currency(app.monthlyVolumeUSD) + "/mo"} />
            </dl>
          </div>

          {/* Source-of-cards */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Source-of-cards declaration</h2>
            <p className="mt-3 text-body-sm text-ink-muted leading-relaxed bg-surface-alt border border-line rounded-md p-4">
              {app.sourceOfCards || "(none provided)"}
            </p>
          </div>

          {/* KYC result panel */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-h3 text-ink font-semibold">Identity verification (KYC)</h2>
              <KycBadge status={app.kycStatus} />
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              <KycCheck label="ID document" pass={app.kycStatus === "Passed"} fail={app.kycStatus === "Failed"} />
              <KycCheck label="Selfie match" pass={app.kycStatus === "Passed"} fail={app.kycStatus === "Failed"} />
              <KycCheck label="Sanctions / PEP" pass={app.kycStatus === "Passed" || app.kycStatus === "InProgress"} />
            </div>
            {app.kycStatus === "Failed" && (
              <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
                <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
                Veriff returned ID-doc-verification-failed. Recommend asking applicant to retry with better lighting.
              </div>
            )}
          </div>

          {app.rejectReason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <div className="eyebrow text-red-800 mb-1.5">Rejected</div>
              <p className="text-body-sm text-red-900 leading-relaxed">{app.rejectReason}</p>
            </div>
          )}
        </div>

        {/* Side: review notes */}
        <aside className="space-y-5">
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-3">Review history</div>
            {app.reviewNotes.length === 0 ? (
              <p className="text-caption text-ink-subtle">No notes yet. The first reviewer will leave a note when they pick this up.</p>
            ) : (
              <ol className="space-y-4">
                {app.reviewNotes.map((n, i) => {
                  const a = getAdminById(n.actor);
                  return (
                    <li key={i} className="text-body-sm">
                      <div className="text-caption text-ink-subtle">{format.relative(n.at)} · {a?.firstName} {a?.lastName[0]}.</div>
                      <p className="mt-1 text-ink-muted leading-relaxed">{n.text}</p>
                    </li>
                  );
                })}
              </ol>
            )}
            <Textarea placeholder="Add an internal note…" className="mt-4 min-h-[88px] text-caption" />
            <Button variant="outline" size="sm" className="mt-2 w-full">Save note</Button>
          </div>
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-2">Reviewer</div>
            <div className="text-body-sm text-ink">
              {reviewer ? `${reviewer.firstName} ${reviewer.lastName}` : <span className="text-ink-hint">Unassigned</span>}
            </div>
            <div className="text-caption text-ink-subtle">{reviewer?.role || ""}</div>
          </div>
        </aside>
      </div>

      {/* Approve modal */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {app.firstName} {app.lastName}?</DialogTitle>
            <DialogDescription>
              They&rsquo;ll receive a welcome email with a link to start KYC. Once KYC and bank capture are complete, they&rsquo;ll be active members.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Optional welcome note…" className="min-h-[100px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approve} data-testid="confirm-approve-app-btn" className="bg-brand-700 hover:bg-brand-800 text-white">Approve and send KYC</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {app.firstName} {app.lastName}?</DialogTitle>
            <DialogDescription>The applicant will be notified by email. The reason below appears in the audit log.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-caption font-semibold text-ink mb-1.5 block">Reason template</label>
            <Select value={rejectTpl} onValueChange={onTpl}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REJECT_TEMPLATES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea value={rejectText} onChange={(e) => setRejectText(e.target.value)} className="min-h-[120px]" required />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={reject} disabled={!rejectText.trim()} data-testid="confirm-reject-app-btn" className="bg-red-600 hover:bg-red-700 text-white">Reject application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-caption text-ink-hint mb-0.5">{icon}{label}</div>
      <div className="text-ink">{value}</div>
    </div>
  );
}

function KycBadge({ status }) {
  const map = {
    NotStarted: { label: "Not started", tone: "neutral" },
    InProgress: { label: "In progress", tone: "info" },
    Passed:     { label: "Passed",      tone: "success" },
    Failed:     { label: "Failed",      tone: "danger" },
  };
  const s = map[status] || map.NotStarted;
  return <StatusBadge tone={s.tone} dot>{s.label}</StatusBadge>;
}

function KycCheck({ label, pass, fail }) {
  return (
    <div className={`p-3 rounded-md border text-caption ${pass ? "bg-emerald-50 border-emerald-200" : fail ? "bg-red-50 border-red-200" : "bg-surface-alt border-line"}`}>
      <div className="flex items-center gap-1.5">
        {pass ? <CheckCircle2 className="size-3.5 text-emerald-600" /> :
         fail ? <XCircle className="size-3.5 text-red-600" /> :
                <AlertCircle className="size-3.5 text-ink-hint" />}
        <span className="font-semibold text-ink">{label}</span>
      </div>
      <div className={`mt-0.5 ${pass ? "text-emerald-700" : fail ? "text-red-700" : "text-ink-subtle"}`}>
        {pass ? "Passed" : fail ? "Failed" : "Not started"}
      </div>
    </div>
  );
}
